/**
 * HITL (Human-in-the-Loop) API Service
 */
import api from './api';

export interface HITLRequest {
  id: string;
  trajectory_id: string;
  decision_point_id: string;
  instance_id?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'auto_approved';
  context?: string;
  options?: string[];
  iteration: number;
  created_at: string;
  expires_at: string;
  timeout_action: 'fail' | 'continue' | 'auto_approve';
  responded_by?: string;
  responded_at?: string;
  response?: Record<string, unknown>;
  checkpoint_id?: string;
  thread_id?: string;
  metadata?: Record<string, unknown>;
}

export interface HITLStats {
  pending_count: number;
  approved_today: number;
  rejected_today: number;
  expired_today: number;
  avg_response_time_hours?: number;
}

export interface HITLResponsePayload {
  response: Record<string, unknown>;
  notes?: string;
}

/**
 * Fetch pending HITL requests
 */
export async function fetchPendingHITLRequests(
  limit: number = 50,
  offset: number = 0
): Promise<HITLRequest[]> {
  const response = await api.get('/hitl/pending', {
    params: { limit, offset },
  });
  return response.data;
}

/**
 * Fetch all HITL requests with optional filters
 */
export async function fetchHITLRequests(params: {
  status?: string;
  trajectory_id?: string;
  limit?: number;
  offset?: number;
}): Promise<HITLRequest[]> {
  const response = await api.get('/hitl/all', { params });
  return response.data;
}

/**
 * Fetch HITL requests for a specific trajectory
 */
export async function fetchTrajectoryHITLRequests(
  trajectory_id: string
): Promise<HITLRequest[]> {
  const response = await api.get('/hitl/all', {
    params: { trajectory_id },
  });
  return response.data;
}

/**
 * Fetch a single HITL request by ID
 */
export async function fetchHITLRequest(request_id: string): Promise<HITLRequest> {
  const response = await api.get(`/hitl/${request_id}`);
  return response.data;
}

/**
 * Fetch HITL statistics
 */
export async function fetchHITLStats(): Promise<HITLStats> {
  const response = await api.get('/hitl/stats');
  return response.data;
}

/**
 * Respond to (approve) a HITL request
 */
export async function respondToHITLRequest(
  request_id: string,
  payload: HITLResponsePayload
): Promise<{ status: string; message: string; hitl_request_id: string }> {
  const response = await api.post(`/hitl/${request_id}/respond`, payload);
  return response.data;
}

/**
 * Reject a HITL request
 */
export async function rejectHITLRequest(
  request_id: string,
  reason?: string
): Promise<{ status: string; message: string; hitl_request_id: string }> {
  const response = await api.post(`/hitl/${request_id}/reject`, null, {
    params: { reason },
  });
  return response.data;
}
